import {
  CHANNEL_TYPE_LABELS,
  CONTENT_FORMAT_LABELS,
  PILLAR_LABELS,
  daysUntil,
  formatDate,
  formatDateTime,
  type ContentItemDto,
} from '@nova/shared';
import { CalendarClock, Clock } from 'lucide-react';
import { CompanyMark } from '../../components/CompanyMark.js';
import { AgentMark, DemoMark, MetaChip, StatusBadge } from '../../components/StatusBadge.js';
import { useAreaColor } from '../../lib/area-color.js';
import { cn } from '../../lib/cn.js';

/**
 * One line in the editorial plan and in the dashboard lists. The left edge
 * carries the area colour so scanning by product area works at a glance.
 */
export function ContentRow({
  item,
  onOpen,
  showArea = true,
  compact = false,
}: {
  item: ContentItemDto;
  onOpen: (item: ContentItemDto) => void;
  showArea?: boolean;
  compact?: boolean;
}) {
  const areaColor = useAreaColor();
  const deadline = deadlineFor(item);

  // The channels this card targets, by name (or channel type as a fallback).
  // First couple + «+N» keeps the row from wrapping into a wall of chips.
  const channelNames = (item.channelTargets ?? [])
    .map((t) => t.channel?.name ?? (t.channel?.type ? CHANNEL_TYPE_LABELS[t.channel.type] : null))
    .filter((name): name is string => Boolean(name));
  const shownChannels = channelNames.slice(0, 2);
  const extraChannels = channelNames.length - shownChannels.length;

  return (
    <button
      type="button"
      data-testid="content-row"
      onClick={() => onOpen(item)}
      className={cn(
        'bg-panel/40 group relative flex w-full items-start gap-3 overflow-hidden rounded-lg border border-transparent px-3 text-left transition',
        'hover:border-hairline hover:bg-raised/60',
        compact ? 'py-2.5' : 'py-3',
      )}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ backgroundColor: areaColor(item.area?.color) }}
      />

      <div className="min-w-0 flex-1 pl-1">
        <div className="flex items-start justify-between gap-3">
          <p className="text-ink group-hover:text-accent min-w-0 truncate text-sm">{item.title}</p>
          <StatusBadge status={item.status} overdue={item.isOverdue} size="sm" />
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {showArea && item.area && <MetaChip color={item.area.color}>{item.area.name}</MetaChip>}
          {!compact && <MetaChip>{PILLAR_LABELS[item.pillar]}</MetaChip>}
          <MetaChip>{CONTENT_FORMAT_LABELS[item.format]}</MetaChip>
          {!compact && shownChannels.map((name) => <MetaChip key={name}>{name}</MetaChip>)}
          {!compact && extraChannels > 0 && (
            <MetaChip className="text-ink-soft/80">+{extraChannels}</MetaChip>
          )}
          {item.source === 'AGENT' && <AgentMark />}
          {item.isSeed && <DemoMark />}
          {deadline && (
            <span
              className={cn(
                'inline-flex items-center gap-1 text-[11px]',
                deadline.tone === 'danger'
                  ? 'text-danger'
                  : deadline.tone === 'warning'
                    ? 'text-warning'
                    : 'text-ink-soft',
              )}
            >
              {deadline.icon === 'clock' ? (
                <Clock size={11} aria-hidden />
              ) : (
                <CalendarClock size={11} aria-hidden />
              )}
              {deadline.label}
            </span>
          )}
        </div>
      </div>

      <span className="pointer-events-none absolute bottom-2 right-2.5">
        <CompanyMark company={item.area?.company} />
      </span>
    </button>
  );
}

/** Which date matters right now, and how urgent it is. */
export function deadlineFor(item: ContentItemDto): {
  label: string;
  tone: 'neutral' | 'warning' | 'danger';
  icon: 'clock' | 'calendar';
} | null {
  // Finished items have no deadline left to miss — they state what happened.
  if (item.status === 'PUBLISHED') {
    return item.publishAt
      ? { label: `Publisert ${formatDate(item.publishAt)}`, tone: 'neutral', icon: 'calendar' }
      : null;
  }
  if (item.status === 'REJECTED' || item.status === 'CANCELLED') return null;

  const usesDraftDeadline = ['SUGGESTED', 'APPROVED', 'DRAFT'].includes(item.status);
  const date = usesDraftDeadline ? item.draftDeadline : item.publishAt;
  if (!date) return null;

  const days = daysUntil(date);
  const tone = days < 0 ? 'danger' : days <= 2 ? 'warning' : 'neutral';

  const label = usesDraftDeadline
    ? days < 0
      ? `Frist gikk ut ${formatDate(date)}`
      : days === 0
        ? 'Frist i dag'
        : days === 1
          ? 'Frist i morgen'
          : `Frist ${formatDate(date)}`
    : days < 0
      ? `Skulle vært publisert ${formatDate(date)}`
      : days === 0
        ? `Publiseres i dag ${formatDateTime(date).split(' ').pop()}`
        : `Publiseres ${formatDate(date)}`;

  return { label, tone, icon: usesDraftDeadline ? 'clock' : 'calendar' };
}
